import { WeeklyPeriod } from "@swivl-mono/types";

/** `<input type="time">` wants zero-padded 24h. */
export function minutesToHHMM(m: number): string {
  const h = Math.floor((m % 1440) / 60);
  const min = m % 60;
  return `${String(h).padStart(2, "0")}:${String(min).padStart(2, "0")}`;
}

export function hhmmToMinutes(s: string): number {
  const [h, m] = s.split(":").map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
}

/** Human-facing 12h time, e.g. `9:00 AM`, `1:30 PM`. */
export function formatMinute(m: number): string {
  const wrapped = m % 1440;
  let h = Math.floor(wrapped / 60);
  const min = wrapped % 60;
  const period = h >= 12 ? "PM" : "AM";
  if (h === 0) h = 12;
  else if (h > 12) h -= 12;
  return min === 0 ? `${h}:00 ${period}` : `${h}:${String(min).padStart(2, "0")} ${period}`;
}

/** One open window as read-only text, e.g. `9:00 AM – 6:00 PM`. */
export function formatPeriod(period: { openMinute: number; closeMinute: number }): string {
  return `${formatMinute(period.openMinute)} – ${formatMinute(period.closeMinute)}`;
}

/** Several windows on one day, e.g. `9:00 AM – 12:00 PM, 1:00 PM – 5:00 PM`. */
export function formatPeriods(periods: { openMinute: number; closeMinute: number }[]): string {
  return periods.map(formatPeriod).join(", ");
}

/** Shown when a day's windows overlap; saving is blocked until it's resolved. */
export const OVERLAP_ERROR = "These windows overlap. Change a time or remove a window.";

/**
 * Default times for a newly added window: starting 1 hour after the day's last
 * window ends and lasting 1 hour, so adding a window doesn't immediately
 * overlap an existing one. `fallback` is used when the day has no windows yet.
 * Kept within the day (latest 11:00 PM - 11:59 PM); a day with no room left
 * can still overlap, which the overlap error then flags.
 */
export function nextWindowAfter(
  periods: { openMinute: number; closeMinute: number }[],
  fallback: { openMinute: number; closeMinute: number }
): { openMinute: number; closeMinute: number } {
  if (periods.length === 0) return { ...fallback };
  const lastClose = Math.max(...periods.map((p) => p.closeMinute));
  const openMinute = Math.min(lastClose + 60, 23 * 60);
  return { openMinute, closeMinute: Math.min(openMinute + 60, 24 * 60 - 1) };
}

export const DAY_ABBR: Record<number, string> = { 0: "Sun", 1: "Mon", 2: "Tue", 3: "Wed", 4: "Thu", 5: "Fri", 6: "Sat" };

/** Weeks read Monday-first here — these are business hours, not calendars. */
const DISPLAY_ORDER = [1, 2, 3, 4, 5, 6, 0];

/** True when Monday has at least one open window — nothing to copy otherwise. */
export function hasMondayHours(periods: WeeklyPeriod[]): boolean {
  return periods.some((p) => p.dayOfWeek === 1);
}

/**
 * Monday's open windows applied across Tue–Fri, leaving the weekend alone.
 * Returns the input untouched when Monday is closed, so the caller can't
 * silently wipe the working week.
 */
export function copyMondayToWeekdays(periods: WeeklyPeriod[]): WeeklyPeriod[] {
  const monday = periods.filter((p) => p.dayOfWeek === 1);
  if (monday.length === 0) return periods;
  const next = periods.filter((p) => p.dayOfWeek < 1 || p.dayOfWeek > 5);
  for (const dow of [1, 2, 3, 4, 5] as const) {
    for (const p of monday) next.push({ ...p, dayOfWeek: dow });
  }
  return next;
}

export interface ScheduleSummaryRow {
  /** `Mon`, `Tue`, … */
  day: string;
  /** `9:00 AM – 6:00 PM` or `Closed`. */
  hours: string;
}

export const CLOSED = "Closed";

function hoursForDay(periods: WeeklyPeriod[], dow: number): string {
  const forDay = periods.filter((p) => p.dayOfWeek === dow).sort((a, b) => a.openMinute - b.openMinute);
  return forDay.length === 0 ? CLOSED : formatPeriods(forDay);
}

/**
 * A week as one row per day, Monday first — the same seven rows the edit grid
 * shows, so switching in and out of edit mode keeps the days in place.
 */
export function summarizeWeek(periods: WeeklyPeriod[]): ScheduleSummaryRow[] {
  return DISPLAY_ORDER.map((dow) => ({ day: DAY_ABBR[dow]!, hours: hoursForDay(periods, dow) }));
}
